import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Activity, Compass, Users, BookOpen, Handshake, Calendar, Heart,
  FileText, MessageSquare, Settings, BookmarkCheck, Bell, Play, Trash2,
  Sparkles, Search, Megaphone,
} from 'lucide-react';
import { DashboardShell } from '../components/brand/DashboardShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SavedSearches() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      // Fire alert check first so new-match counts are fresh, then list.
      await axios.post(`${API}/saved-searches/check-alerts`, {}, getAuthHeader()).catch(() => {});
      const { data } = await axios.get(`${API}/saved-searches`, getAuthHeader());
      setSearches(data.saved_searches || []);
    } catch (e) {
      toast.error('Could not load saved searches');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const runAgain = (s) => {
    navigate('/patient/find-clinicians', { state: { prefillFilters: s.filters, autoRun: true, savedName: s.name } });
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/saved-searches/${id}`, getAuthHeader());
      setSearches((prev) => prev.filter((s) => s.id !== id));
      toast.success('Saved search removed');
    } catch (e) {
      toast.error('Could not delete');
    }
  };

  const toggleAlerts = async (s) => {
    const next = !s.alerts_enabled;
    setSearches((prev) => prev.map((x) => (x.id === s.id ? { ...x, alerts_enabled: next } : x)));
    try {
      await axios.patch(`${API}/saved-searches/${s.id}`, { alerts_enabled: next }, getAuthHeader());
    } catch (e) {
      toast.error('Could not update alerts');
      setSearches((prev) => prev.map((x) => (x.id === s.id ? { ...x, alerts_enabled: !next } : x)));
    }
  };

  const navItems = [
    { icon: Activity, label: 'Dashboard', href: '/patient/dashboard' },
    { icon: Compass, label: 'Care Navigator', href: '/patient/navigator', highlight: true },
    { icon: Users, label: 'Find Clinicians', href: '/patient/find-clinicians' },
    { icon: BookmarkCheck, label: 'Saved Searches', active: true },
    { icon: Megaphone, label: 'Post a Need', href: '/patient/care-requests' },
    { icon: BookOpen, label: 'Browse Marketplace', href: '/marketplace' },
    { icon: Handshake, label: 'Offers & Bookings', href: '/patient/offers' },
    { icon: Calendar, label: 'Appointments', href: '/patient/dashboard#appointments' },
    { icon: Heart, label: 'Daily Check-in', href: '/patient/checkin' },
    { icon: Users, label: 'Family Portal', href: '/patient/family' },
    { icon: FileText, label: 'Documents', href: '/patient/documents' },
    { icon: MessageSquare, label: 'Messages', href: '/patient/messages' },
    { icon: Settings, label: 'Settings', href: '#' },
  ];

  return (
    <DashboardShell
      navItems={navItems}
      title="Saved searches"
      subtitle="We'll quietly watch the marketplace and alert you the moment a new clinician matches what you're looking for."
      rightSlot={
        <Button onClick={() => navigate('/patient/find-clinicians')} className="cn-btn cn-btn-primary" data-testid="new-search-cta">
          <Search className="w-4 h-4" /> New search
        </Button>
      }
      testId="saved-searches-page"
    >
      {loading ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {[1, 2].map((i) => <div key={i} className="cn-card p-6 h-40 animate-pulse bg-cn-bg/60" />)}
        </div>
      ) : searches.length === 0 ? (
        <div className="cn-card p-12 text-center bg-gradient-to-br from-white to-cn-bg/60" data-testid="saved-searches-empty">
          <div className="w-20 h-20 rounded-3xl bg-cn-primary/10 text-cn-primary mx-auto flex items-center justify-center mb-4">
            <BookmarkCheck className="w-10 h-10" />
          </div>
          <h3 className="cn-display text-2xl text-cn-text dark:text-white">No saved searches yet</h3>
          <p className="text-sm text-cn-text-muted mt-2 max-w-md mx-auto">
            Run a search on Find Clinicians, then tap <span className="font-semibold text-cn-text">"Save this search & alert me"</span> to get notified when new matches appear.
          </p>
          <Button onClick={() => navigate('/patient/find-clinicians')} className="cn-btn cn-btn-primary mt-5" data-testid="empty-find-cta">
            <Search className="w-4 h-4" /> Find clinicians
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {searches.map((s) => (
            <div key={s.id} className="cn-card bg-white dark:bg-[#0F172A] border border-cn-border/70 p-6" data-testid={`saved-search-${s.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="cn-display text-lg text-cn-text dark:text-white truncate">{s.name}</h3>
                    {s.new_match_count > 0 && (
                      <Badge className="bg-cn-accent/15 text-cn-accent border border-cn-accent/30" data-testid={`new-match-badge-${s.id}`}>
                        <Sparkles className="w-3 h-3 mr-1" /> {s.new_match_count} new
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-cn-text-muted mt-1">{s.summary}</p>
                </div>
                <div className="text-center px-3 py-2 rounded-2xl bg-cn-primary/8 flex-shrink-0">
                  <p className="font-display font-extrabold text-2xl leading-none text-cn-primary">{s.match_count}</p>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted mt-0.5">matches</p>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between gap-3 p-3 rounded-2xl bg-cn-bg/60 border border-cn-border/50">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Bell className={`w-4 h-4 ${s.alerts_enabled ? 'text-cn-primary' : 'text-cn-text-muted'}`} />
                  <span className="text-xs font-semibold text-cn-text">New-match alerts</span>
                </label>
                <Switch
                  checked={s.alerts_enabled}
                  onCheckedChange={() => toggleAlerts(s)}
                  data-testid={`alerts-toggle-${s.id}`}
                />
              </div>

              <div className="mt-4 flex gap-2">
                <Button onClick={() => runAgain(s)} className="flex-1 cn-btn cn-btn-primary !py-2" data-testid={`run-again-${s.id}`}>
                  <Play className="w-4 h-4" /> Run again
                </Button>
                <Button onClick={() => remove(s.id)} className="cn-btn cn-btn-ghost !py-2 text-cn-error" data-testid={`delete-search-${s.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}
